import {
  Grid,
  Container,
  Box,
  Typography
} from '@mui/material';
import MissingContent from '../../../../components/shared/errorMessages/MissingContent';
import { GalleryMediaItem } from '../../types/CMSTypes';

interface CMSConsumerGalleryProps {
  galleryMedia: GalleryMediaItem[] | null;
  title?: string;
  secondaryText?: string;
  hasTitle?: boolean;
  hasSecondaryText?: boolean;
  marginTop?: number;
  marginBottom?: number;
  // Layout options
  galleryLayout?: 'masonry' | 'standard';
  columns?: number;
}

const CMSConsumerGallery: React.FC<CMSConsumerGalleryProps> = ({
                                                                 galleryMedia,
                                                                 title = '',
                                                                 secondaryText = '',
                                                                 hasTitle = false,
                                                                 hasSecondaryText = false,
                                                                 marginTop = 3,
                                                                 marginBottom = 3,
                                                                 galleryLayout = 'masonry',
                                                                 columns = 2
                                                               }) => {

  // Find image by position
  const findImageByPosition = (position: string) => {
    if (!galleryMedia) return null;
    const image = galleryMedia.find((img: GalleryMediaItem) => img.position === position);
    return image?.file_link;
  };

  // Show missing content if no data
  if (!galleryMedia || galleryMedia.length === 0) {
    return <MissingContent sectionName="Gallery" />;
  }

  return (
    <Container maxWidth="lg">
      {/* Title and Secondary Text */}
      <Grid
        mt={marginTop}
        container
        alignItems="center"
        flexDirection="column"
        sx={{ width: "100%" }}
      >
        {hasTitle && <Typography variant="h4">{title}</Typography>}
        {hasSecondaryText && (
          <Typography variant="h6">{secondaryText}</Typography>
        )}
      </Grid>

      {/* Desktop View - 4 Image Layout */}
      <Box
        sx={{
          display: { xs: "none", lg: "block" },
          mt: marginTop,
          mb: marginBottom
        }}
      >
        <Grid container spacing={'16px'}>
          {/* Left Column */}
          <Grid item xs={6}>
            <Grid container spacing={'16px'}>
              {/* Top Left Image */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    width: '100%',
                    height: '648px',
                    overflow: 'hidden'
                  }}
                >
                  {(() => {
                    const imageUrl = findImageByPosition("top left");
                    return imageUrl ? (
                      <img
                        src={imageUrl}
                        alt="gallery-top-left"
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : null;
                  })()}
                </Box>
              </Grid>

              {/* Bottom Left Image */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    width: '100%',
                    height: '366px',
                    overflow: 'hidden'
                  }}
                >
                  {(() => {
                    const imageUrl = findImageByPosition("bottom left");
                    return imageUrl ? (
                      <img
                        src={imageUrl}
                        alt="gallery-bottom-left"
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : null;
                  })()}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* Right Column */}
          <Grid item xs={6}>
            <Grid container spacing={'16px'}>
              {/* Top Right Image */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    width: '100%',
                    height: '366px',
                    overflow: 'hidden'
                  }}
                >
                  {(() => {
                    const imageUrl = findImageByPosition("top right");
                    return imageUrl ? (
                      <img
                        src={imageUrl}
                        alt="gallery-top-right"
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : null;
                  })()}
                </Box>
              </Grid>

              {/* Bottom Right Image */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    width: '100%',
                    height: '648px',
                    overflow: 'hidden'
                  }}
                >
                  {(() => {
                    const imageUrl = findImageByPosition("bottom right");
                    return imageUrl ? (
                      <img
                        src={imageUrl}
                        alt="gallery-bottom-right"
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : null;
                  })()}
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Mobile View - Single Column */}
      <Box
        sx={{
          display: { xs: "block", lg: "none" },
          mt: marginTop,
          mb: marginBottom,
          px: 2,
        }}
      >
        <Grid container spacing={'16px'}>
          {/* Top Left Image */}
          <Grid item xs={12}>
            {(() => {
              const imageUrl = findImageByPosition("top left");
              return imageUrl ? (
                <img
                  src={imageUrl}
                  alt="gallery-top-left"
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover'
                  }}
                />
              ) : null;
            })()}
          </Grid>

          {/* Bottom Left Image */}
          <Grid item xs={12}>
            {(() => {
              const imageUrl = findImageByPosition("bottom left");
              return imageUrl ? (
                <img
                  src={imageUrl}
                  alt="gallery-bottom-left"
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover'
                  }}
                />
              ) : null;
            })()}
          </Grid>

          {/* Top Right Image */}
          <Grid item xs={12}>
            {(() => {
              const imageUrl = findImageByPosition("top right");
              return imageUrl ? (
                <img
                  src={imageUrl}
                  alt="gallery-top-right"
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover'
                  }}
                />
              ) : null;
            })()}
          </Grid>

          {/* Bottom Right Image */}
          <Grid item xs={12}>
            {(() => {
              const imageUrl = findImageByPosition("bottom right");
              return imageUrl ? (
                <img
                  src={imageUrl}
                  alt="gallery-bottom-right"
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover'
                  }}
                />
              ) : null;
            })()}
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default CMSConsumerGallery;